// ignoring this for the entire file because dnd requires lots of props to be spread
import styled from '@emotion/styled';
import type { DropResult } from '@hello-pangea/dnd';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { useCallback } from 'react';
import ReactDOM from 'react-dom';
import type { FieldErrors } from 'react-hook-form';

import { useTranslate } from '~/shared/context/use-translate';
import type { JobProfileSkill, SkillProficiencyLevel } from '~/shared/data-contract/skill';
import { Badge } from '~/ui/core/badge/Badge';
import { Button } from '~/ui/core/button/Button';
import { IconDelete } from '~/ui/core/icons';

import { useActiveLanguage } from '../../providers/ActiveLanguageProvider';
import type { CompetencyWithSkills, ProfileForm } from '../types';
import { SkillList } from './SkillList';
import { LANGUAGE_SKILLS_ID, WITHOUT_COMPETENCY_ID } from './use-skills-with-compentencies';

const SkillCategoryContainer = styled.div(({ theme }) => ({
    alignItems: 'flex-start',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.s,
    ...theme.typography.body.S.medium,
    width: '100%',
}));

const CategoryTitle = styled.div(({ theme }) => ({
    alignItems: 'center',
    color: theme.colors.black[900],
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'space-between',
    justifyItems: 'space-between',
    overflow: 'hidden',
    position: 'relative',
    textAlign: 'left',
    textOverflow: 'ellipsis',
    textWrap: 'wrap',
    ...theme.typography.body.S.medium,
    whiteSpace: 'wrap',
    width: '100%',
}));

const DragWrapper = styled.div<{ readonly isDragEnabled: boolean }>(({ isDragEnabled }) => ({
    '& > *': {
        cursor: isDragEnabled ? 'grab' : 'default',
    },
    '&:focus-visible': {
        outline: 'none',
    },
    cursor: isDragEnabled ? 'grab' : 'default',
    width: '100%',
}));

const DroppableWrapper = styled.div(() => ({
    width: '100%',
}));

const PlaceholderText = styled.div(({ theme }) => ({
    color: theme.colors.gray[700],
    userSelect: 'none',
}));

const isSystemCategory = (categoryId: string): boolean =>
    categoryId === WITHOUT_COMPETENCY_ID || categoryId === LANGUAGE_SKILLS_ID;

type SkillCategoryProps = {
    readonly category: CompetencyWithSkills;
    readonly commentable: boolean;
    readonly formErrors?: FieldErrors<ProfileForm>;
    readonly isEditMode: boolean;
    readonly itemId?: string;
    readonly newThreadCreationDisabled?: boolean;
    readonly onChangeProficiencyLevel: (skillId: string, level: JobProfileSkill['level']) => void;
    readonly onOpenSkillDetails: (skillId: string, proficiency: SkillProficiencyLevel | null | undefined) => void;
    readonly onRemoveCompetency: (competencyId: string) => void;
    readonly onRemoveSkill: (competencyId: string, skillId: string) => void;
    readonly onSkillChange?: () => void;
};

export const SkillCategory: FunctionComponent<SkillCategoryProps> = ({
    category,
    commentable,
    formErrors,
    isEditMode,
    itemId,
    newThreadCreationDisabled = false,
    onChangeProficiencyLevel,
    onOpenSkillDetails,
    onRemoveCompetency,
    onRemoveSkill,
    onSkillChange,
}) => {
    const translate = useTranslate();
    const { activeLanguage } = useActiveLanguage();
    const isSystemSkillCategory = isSystemCategory(category.id);
    const categoryTitle =
        category.id === LANGUAGE_SKILLS_ID
            ? translate('common.enums.skillType.language')
            : (category.titles.find((title) => title.language === activeLanguage)?.title ?? category.title);

    const removeSkill = useCallback(
        (skillId: string) => {
            onRemoveSkill(category.id, skillId);
        },
        [category.id, onRemoveSkill],
    );

    return (
        <SkillCategoryContainer>
            {category.title !== '' && (
                <CategoryTitle>
                    {categoryTitle}
                    {!is.undefined(category.diff) ? (
                        <Badge variant={category.diff === 'added' ? 'success' : 'error'}>
                            {translate(`components.skills.${category.diff}`)}
                        </Badge>
                    ) : null}
                    {isEditMode && !isSystemSkillCategory && (
                        <Button
                            data-testid="delete-category-button"
                            iconOnly={true}
                            size="sm"
                            variant="tertiary"
                            onClick={() => onRemoveCompetency(category.id)}
                        >
                            <IconDelete size={16} />
                        </Button>
                    )}
                </CategoryTitle>
            )}
            <Droppable droppableId={category.id} isDropDisabled={category.id === LANGUAGE_SKILLS_ID} type="skill">
                {({ droppableProps, innerRef, placeholder }) => (
                    <DroppableWrapper ref={innerRef} {...droppableProps}>
                        {category.skills.length === 0 && isEditMode && !isSystemSkillCategory && (
                            <PlaceholderText>
                                {translate('pages.job-architecture.job-profile-empty-competency-placeholder')}
                            </PlaceholderText>
                        )}
                        <SkillList
                            commentable={commentable}
                            formErrors={formErrors}
                            isInEditMode={isEditMode}
                            itemId={itemId}
                            newThreadCreationDisabled={newThreadCreationDisabled}
                            openSkillDetails={onOpenSkillDetails}
                            skills={category.skills}
                            onChangeProficiencyLevel={onChangeProficiencyLevel}
                            onRemoveSkill={removeSkill}
                            onSkillChange={onSkillChange}
                        />
                        {placeholder}
                    </DroppableWrapper>
                )}
            </Droppable>
        </SkillCategoryContainer>
    );
};

export const CategoryList: FunctionComponent<{
    readonly commentable: boolean;
    readonly competenciesWithSkills: readonly CompetencyWithSkills[];
    readonly formErrors?: FieldErrors<ProfileForm>;
    readonly handleOnDragEnd: (result: DropResult<string>) => void;
    readonly isInEditMode: boolean;
    readonly itemId?: string;
    readonly newThreadCreationDisabled?: boolean;
    readonly onChangeProficiencyLevel: (skillId: string, level: JobProfileSkill['level']) => void;
    readonly onOpenSkillDetails: (skillId: string, proficiency: SkillProficiencyLevel | null | undefined) => void;
    readonly onRemoveCompetency: (competencyId: string) => void;
    readonly onRemoveSkill: (competencyId: string, skillId: string) => void;
    readonly onSkillChange?: () => void;
}> = ({
    commentable,
    competenciesWithSkills,
    formErrors,
    handleOnDragEnd,
    isInEditMode,
    itemId,
    newThreadCreationDisabled = false,
    onChangeProficiencyLevel,
    onOpenSkillDetails,
    onRemoveCompetency,
    onRemoveSkill,
    onSkillChange,
}) => {
    return (
        <DragDropContext onDragEnd={handleOnDragEnd}>
            <Droppable droppableId="competencies" type="competency">
                {({ droppableProps, innerRef, placeholder }) => (
                    <div ref={innerRef} {...droppableProps}>
                        {competenciesWithSkills.map((competency, index) => (
                            <Draggable
                                key={competency.id}
                                draggableId={competency.id}
                                index={index}
                                isDragDisabled={isInEditMode === false || isSystemCategory(competency.id)}
                            >
                                {(dragProvided, snapshot) => {
                                    if (snapshot.isDragging) {
                                        return ReactDOM.createPortal(
                                            <DragWrapper
                                                ref={dragProvided.innerRef}
                                                isDragEnabled={isInEditMode}
                                                {...dragProvided.dragHandleProps}
                                                {...dragProvided.draggableProps}
                                                style={{
                                                    ...dragProvided.draggableProps.style,
                                                    marginBottom: '1rem',
                                                }}
                                            >
                                                <SkillCategory
                                                    key={competency.id}
                                                    category={competency}
                                                    commentable={commentable}
                                                    formErrors={formErrors}
                                                    isEditMode={isInEditMode}
                                                    itemId={itemId}
                                                    newThreadCreationDisabled={newThreadCreationDisabled}
                                                    onChangeProficiencyLevel={onChangeProficiencyLevel}
                                                    onOpenSkillDetails={onOpenSkillDetails}
                                                    onRemoveCompetency={onRemoveCompetency}
                                                    onRemoveSkill={onRemoveSkill}
                                                    onSkillChange={onSkillChange}
                                                />
                                            </DragWrapper>,
                                            document.body,
                                        );
                                    }

                                    return (
                                        <DragWrapper
                                            ref={dragProvided.innerRef}
                                            isDragEnabled={isInEditMode}
                                            {...dragProvided.dragHandleProps}
                                            {...dragProvided.draggableProps}
                                            style={{
                                                ...dragProvided.draggableProps.style,
                                                marginBottom: '1rem',
                                            }}
                                        >
                                            <SkillCategory
                                                key={competency.id}
                                                category={competency}
                                                commentable={commentable}
                                                formErrors={formErrors}
                                                isEditMode={isInEditMode}
                                                itemId={itemId}
                                                newThreadCreationDisabled={newThreadCreationDisabled}
                                                onChangeProficiencyLevel={onChangeProficiencyLevel}
                                                onOpenSkillDetails={onOpenSkillDetails}
                                                onRemoveCompetency={onRemoveCompetency}
                                                onRemoveSkill={onRemoveSkill}
                                                onSkillChange={onSkillChange}
                                            />
                                        </DragWrapper>
                                    );
                                }}
                            </Draggable>
                        ))}
                        {placeholder}
                    </div>
                )}
            </Droppable>
        </DragDropContext>
    );
};
